/**
 * Story 3.16.1 — Registry de display formatters para listagem read-only
 * em `/admin/settings`. Padrão espelha `settings-categories.ts` (per-key
 * metadata).
 *
 * Decisão: registry pattern em vez de switch gigante no Settings.tsx —
 * adicionar formatter novo = adicionar 1 entrada no map; fallback
 * automático (JsonFallbackDisplay) para keys sem custom.
 */
import { createElement, type ReactElement } from 'react'

import { BusinessHoursDisplay } from '@/components/settings/displays/BusinessHoursDisplay'
import { DurationDisplay } from '@/components/settings/displays/DurationDisplay'
import { EnumDisplay } from '@/components/settings/displays/EnumDisplay'
import { JsonFallbackDisplay } from '@/components/settings/displays/JsonFallbackDisplay'
import { NumberDisplay } from '@/components/settings/displays/NumberDisplay'
import { PhoneListDisplay } from '@/components/settings/displays/PhoneListDisplay'
import { ThresholdDisplay } from '@/components/settings/displays/ThresholdDisplay'

type DisplayFormatter = (value: unknown) => ReactElement

const FORMATTERS: Readonly<Record<string, DisplayFormatter>> = {
  // ---- Operação --------------------------------------------------------
  business_hours: (v) => createElement(BusinessHoursDisplay, { value: v }),
  handoff_sla_minutes: (v) => createElement(DurationDisplay, { value: v, unit: 'minutes' }),
  seller_distribution_rule: (v) =>
    createElement(EnumDisplay, {
      value: v,
      labelMap: { round_robin: 'Round Robin (rotativo)' },
    }),
  ghl_test_phone_allowlist: (v) => createElement(PhoneListDisplay, { value: v }),

  // ---- LGPD ------------------------------------------------------------
  lgpd_retention_days: (v) => createElement(DurationDisplay, { value: v, unit: 'days' }),
  opt_out_ai_confidence_min: (v) => createElement(ThresholdDisplay, { value: v }),

  // ---- OpenAI ----------------------------------------------------------
  openai_default_model: (v) =>
    createElement(EnumDisplay, {
      value: v,
      labelMap: {
        'gpt-4o-mini': 'GPT-4o Mini (rápido + econômico)',
        'gpt-4o': 'GPT-4o (qualidade)',
        'gpt-4-turbo': 'GPT-4 Turbo',
      },
    }),
  openai_fallback_model: (v) =>
    createElement(EnumDisplay, {
      value: v,
      labelMap: {
        'gpt-4o-mini': 'GPT-4o Mini (rápido + econômico)',
        'gpt-4o': 'GPT-4o (qualidade)',
        'gpt-4-turbo': 'GPT-4 Turbo',
      },
    }),
  openai_temperature: (v) => createElement(ThresholdDisplay, { value: v, max: 2 }),
  openai_max_tokens: (v) =>
    createElement(NumberDisplay, { value: v, unitSingular: 'token', unitPlural: 'tokens' }),
  openai_timeout_ms: (v) => createElement(DurationDisplay, { value: v, unit: 'milliseconds' }),

  // ---- Prompts ---------------------------------------------------------
  prompt_max_chars: (v) =>
    createElement(NumberDisplay, { value: v, unitSingular: 'caractere', unitPlural: 'caracteres' }),
  anti_repetition_threshold: (v) => createElement(ThresholdDisplay, { value: v }),

  // ---- Reagendamento ---------------------------------------------------
  reschedule_max: (v) =>
    createElement(NumberDisplay, {
      value: v,
      unitSingular: 'reagendamento',
      unitPlural: 'reagendamentos',
    }),
  reactivation_max: (v) =>
    createElement(NumberDisplay, {
      value: v,
      unitSingular: 'reativação',
      unitPlural: 'reativações',
    }),
  attendance_callback_delay_min: (v) =>
    createElement(DurationDisplay, { value: v, unit: 'minutes' }),

  // ---- Calendar --------------------------------------------------------
  calendar_provider: (v) =>
    createElement(EnumDisplay, {
      value: v,
      labelMap: {
        google: 'Google Calendar',
        ghl: 'GoHighLevel',
      },
    }),
  ghl_location_id: (v) =>
    createElement(EnumDisplay, {
      value: v,
      // Sem labelMap — exibe o ID raw com ícone Tag. Display semântico se o
      // valor for vazio: EnumDisplay renderiza "" (espaço vazio), o que é OK
      // — preview mostra a key e o setting display fica visual claro.
    }),
}

/**
 * Returns the read-only display ReactElement for a setting. Falls back
 * to JsonFallbackDisplay (JSON pretty-printed) when the key has no
 * registered formatter — preserves backwards compat for keys not yet
 * mapped (ex: handoff_operators, reactivation_thresholds, prompt_banned_patterns,
 * opt_in_text, last_assigned_seller_id).
 */
export function formatSettingDisplay(key: string, value: unknown): ReactElement {
  const formatter = FORMATTERS[key]
  if (formatter !== undefined) return formatter(value)
  return createElement(JsonFallbackDisplay, { value })
}

/**
 * Set de keys que TÊM formatter customizado — útil para tests verificarem
 * coverage (e para o Settings.tsx decidir se aplica padding extra na linha).
 */
export const KEYS_WITH_CUSTOM_DISPLAY: ReadonlySet<string> = new Set(Object.keys(FORMATTERS))
